<script setup lang="ts">
import { onMounted, ref } from "vue";

import { SubmissionJob } from "~/classes/models/submission-job/submission-job.model";
import type { OAuthLicensesResponse } from "~/server/utils/oauth/oauth.api.utils";
import { SKIP_LOGOUT_REDIRECT_ONCE_KEY } from "~/utils/oauth-logout-redirect";
import { getCompanionLicenseUnlockState } from "~/utils/companion-license-unlock";

type ActiveLicensesDebugResponse =
  | (OAuthLicensesResponse & {
      oauthDebug?: {
        hasAccessToken: boolean;
        hasRefreshToken: boolean;
        expiryTimestamp?: number;
        expiresAt?: string;
        nowTimestamp: number;
        now: string;
        msUntilExpiry?: number;
        isAccessTokenExpired?: boolean;
      };
      transferredLicenseAssignmentIds?: number[];
    })
  | {
      user: undefined;
      summary: undefined;
      licenses: [];
      error: string;
      licenseFetchError?: unknown;
      oauthDebug?: {
        hasAccessToken: boolean;
        hasRefreshToken: boolean;
        expiryTimestamp?: number;
        expiresAt?: string;
        nowTimestamp: number;
        now: string;
        msUntilExpiry?: number;
        isAccessTokenExpired?: boolean;
      };
      transferredLicenseAssignmentIds?: number[];
    };

const emit = defineEmits([
  "start-write",
  "upload-computer",
  "upload-drive",
  "select-checklist",
]);

const companionStore = useCompanion();
const { profiles, selectedProfile, selectedProfileId } =
  storeToRefs(companionStore);
const levelsStore = useLevels();
const { allLevels } = storeToRefs(levelsStore);
const runtimeConfig = useRuntimeConfig();

const selectedChecklistId = ref<string | undefined>(undefined);
const activeLicensesDebugResponse = ref<
  ActiveLicensesDebugResponse | undefined
>();
const activeLicensesDebugError = ref<string | undefined>();
const isLoadingActiveLicensesDebug = ref(false);
const isApplyingLicense = ref(false);
const isActivatingLicense = ref(false);
const curriculumChecklists = ref<Checklist[]>([]);
const hasLoadedCurriculumChecklists = ref(false);

const handleChecklistChange = (id: string | undefined) => {
  selectedChecklistId.value = id;
  emit("select-checklist", id);
};

const handleChecklistsUpdated = ({
  checklists,
  isLoaded,
}: {
  checklists: Checklist[];
  isLoaded: boolean;
}) => {
  curriculumChecklists.value = checklists;
  hasLoadedCurriculumChecklists.value = isLoaded;
};

const sortedCurriculumChecklists = computed(() => {
  return useSortChecklists(curriculumChecklists.value);
});

const selectedChecklist = computed(() => {
  return (
    sortedCurriculumChecklists.value.find(
      (c) => c.id === selectedChecklistId.value
    ) ??
    useUserChecklistsStore().checklists.find(
      (c) => c.id === selectedChecklistId.value
    )
  );
});

const isCreatingDocument = ref(false);

const isCompanionBillingEnabled = computed(() => {
  const companionBilling = runtimeConfig.public.companionBilling as
    | { enabled?: boolean }
    | undefined;

  return companionBilling?.enabled === true;
});

const selectedCurriculum = computed(() => {
  return allLevels.value.find(
    (level) => level.id === selectedProfile.value?.curriculumLevelId
  );
});

const selectedCurriculumSku = computed(() => {
  return selectedCurriculum.value?.external_product_sku;
});

const activeLicenses = computed(() => {
  return activeLicensesDebugResponse.value?.licenses ?? [];
});

const shouldShowNoLicensesIntro = computed(() => {
  return (
    isCompanionBillingEnabled.value &&
    !isLoadingActiveLicensesDebug.value &&
    activeLicensesDebugError.value == undefined &&
    activeLicenses.value.length === 0
  );
});

const isLicenseUnexpired = (
  license: OAuthLicensesResponse["licenses"][number]
) => {
  if (license.status !== "active") return false;
  if (!license.expiration_time) return true;

  return new Date(license.expiration_time).getTime() > Date.now();
};

const isLicenseEligible = (
  license: OAuthLicensesResponse["licenses"][number]
) => {
  return license.available_to_activate === true || isLicenseUnexpired(license);
};

const assignedLicenseAssignmentIds = computed(() => {
  return new Set(
    [
      ...profiles.value
        .filter((profile) => profile.id !== selectedProfileId.value)
        .map((profile) => profile.licenseAssignmentId)
        .filter((licenseAssignmentId) => licenseAssignmentId != undefined),
      ...(activeLicensesDebugResponse.value
        ?.transferredLicenseAssignmentIds ?? []),
    ]
  );
});

const matchingEligibleLicense = computed(() => {
  if (!selectedCurriculumSku.value) return undefined;

  return activeLicenses.value.find((license) => {
    return (
      license.license_name === selectedCurriculumSku.value &&
      !assignedLicenseAssignmentIds.value.has(license.id) &&
      isLicenseEligible(license)
    );
  });
});

const hasMatchingEligibleLicense = computed(() => {
  return matchingEligibleLicense.value != undefined;
});

const transferableLicenseAssignmentId = computed(() => {
  return (
    selectedProfile.value?.licenseAssignmentId ??
    matchingEligibleLicense.value?.id
  );
});

const hasLinkedMatchingLicense = computed(() => {
  return (
    !!selectedCurriculumSku.value &&
    selectedProfile.value?.licenseAssignmentId != undefined &&
    selectedProfile.value?.licenseCurriculumSku ===
      selectedCurriculumSku.value &&
    selectedProfile.value?.licenseCurriculumLevelId ===
      selectedCurriculum.value?.id
  );
});

const linkedOAuthLicense = computed(() => {
  if (!hasLinkedMatchingLicense.value) return undefined;

  return activeLicenses.value.find((license) => {
    return license.id === selectedProfile.value?.licenseAssignmentId;
  });
});

const linkedOAuthLicenseMatchesCurriculum = computed(() => {
  return (
    linkedOAuthLicense.value != undefined &&
    linkedOAuthLicense.value.license_name === selectedCurriculumSku.value
  );
});

const hasActiveLinkedMatchingLicense = computed(() => {
  return (
    linkedOAuthLicenseMatchesCurriculum.value &&
    linkedOAuthLicense.value != undefined &&
    isLicenseUnexpired(linkedOAuthLicense.value)
  );
});

const formatLicenseDate = (expirationTime?: string | null) => {
  if (!expirationTime) return undefined;

  const expirationDate = new Date(expirationTime);

  if (Number.isNaN(expirationDate.getTime())) return undefined;

  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(expirationDate);
};

const isLinkedOAuthLicenseExpired = computed(() => {
  if (!linkedOAuthLicenseMatchesCurriculum.value || !linkedOAuthLicense.value) {
    return false;
  }

  if (linkedOAuthLicense.value.status === "expired") return true;

  const expirationTime = linkedOAuthLicense.value.expiration_time;

  if (!expirationTime) return false;

  return new Date(expirationTime).getTime() <= Date.now();
});

const linkedLicenseStatusLabel = computed(() => {
  if (hasActiveLinkedMatchingLicense.value) {
    const expirationLabel = formatLicenseDate(
      linkedOAuthLicense.value?.expiration_time
    );

    return expirationLabel ? `Expires ${expirationLabel}` : undefined;
  }

  if (isLinkedOAuthLicenseExpired.value) {
    const expirationLabel = formatLicenseDate(
      linkedOAuthLicense.value?.expiration_time
    );

    return expirationLabel ? `Expired on ${expirationLabel}` : "Expired";
  }

  return undefined;
});

const canChangeCurriculumInline = computed(() => {
  return (
    selectedProfile.value?.curriculumLevelId == undefined ||
    isLinkedOAuthLicenseExpired.value
  );
});

const trialChecklistCount = computed(() => {
  return Math.max(0, selectedCurriculum.value?.numTrialChecklistsIncluded ?? 0);
});

const isSelectedChecklistOutsideTrial = computed(() => {
  if (!selectedChecklistId.value) return false;
  if (!hasLoadedCurriculumChecklists.value) return false;

  const selectedChecklistIndex = sortedCurriculumChecklists.value.findIndex(
    (checklist) => checklist.id === selectedChecklistId.value
  );

  if (selectedChecklistIndex === -1) return false;

  return (
    trialChecklistCount.value <= 0 ||
    selectedChecklistIndex >= trialChecklistCount.value
  );
});

const shouldShowLicenseUnlockOverlay = computed(() => {
  if (!isCompanionBillingEnabled.value) return false;
  if (!selectedProfileId.value) return false;
  if (!selectedChecklistId.value) return false;
  if (!selectedCurriculum.value) return false;
  if (!isSelectedChecklistOutsideTrial.value) return false;
  if (hasActiveLinkedMatchingLicense.value) return false;

  return true;
});

const licenseUnlockState = computed(() => {
  return getCompanionLicenseUnlockState({
    isSelectedChecklistOutsideTrial: isSelectedChecklistOutsideTrial.value,
    isLoadingLicenses: isLoadingActiveLicensesDebug.value,
    hasLicenseLoadError: activeLicensesDebugError.value != undefined,
    matchingEligibleLicense: matchingEligibleLicense.value,
    linkedOAuthLicense: linkedOAuthLicense.value,
    linkedOAuthLicenseMatchesCurriculum:
      linkedOAuthLicenseMatchesCurriculum.value,
    selectedProfileLicenseAssignmentId:
      selectedProfile.value?.licenseAssignmentId,
  });
});

const canActivateLinkedLicense = computed(() => {
  return licenseUnlockState.value.canActivateLinkedLicense;
});

const canApplyAvailableLicense = computed(() => {
  return licenseUnlockState.value.canApplyAvailableLicense;
});

const hasLicenseUnlockAction = computed(() => {
  return licenseUnlockState.value.hasLicenseUnlockAction;
});

const isPurchaseRequired = computed(() => {
  return licenseUnlockState.value.isPurchaseRequired;
});

const licenseUnlockTitle = computed(() => {
  return isPurchaseRequired.value
    ? "Purchase Required"
    : "Begin using IEW Writing Companion";
});

const licenseUnlockMessage = computed(() => {
  const levelName = selectedCurriculum.value?.name ?? "this curriculum";

  if (isLoadingActiveLicensesDebug.value) {
    return `Checking licenses for ${levelName}...`;
  }

  if (activeLicensesDebugError.value) {
    return "Unable to check licenses right now.";
  }

  if (hasLicenseUnlockAction.value) {
    return `Once you click begin, you have 365 days to access ${levelName} Writing Companion.`;
  }

  return `You have access to the first three checklists in this curriculum. Purchase ${levelName} Writing Companion from IEW.com to access this checklist.`;
});

const licenseUnlockActionLabel = computed(() => {
  return hasLicenseUnlockAction.value ? "Begin" : undefined;
});

const licenseUnlockSecondaryActionLabel = computed(() => {
  return isPurchaseRequired.value ? "Purchase Now" : undefined;
});

const isLicenseUnlockActionLoading = computed(() => {
  return isApplyingLicense.value || isActivatingLicense.value;
});

const activeLicensesDebugJson = computed(() => {
  return JSON.stringify(
    {
      selectedProfileId: selectedProfileId.value,
      selectedProfileLicenseAssignmentId:
        selectedProfile.value?.licenseAssignmentId,
      selectedCurriculumId: selectedCurriculum.value?.id,
      selectedCurriculumName: selectedCurriculum.value?.name,
      selectedCurriculumSku: selectedCurriculumSku.value,
      selectedChecklistId: selectedChecklistId.value,
      isCompanionBillingEnabled: isCompanionBillingEnabled.value,
      hasLoadedCurriculumChecklists: hasLoadedCurriculumChecklists.value,
      isSelectedChecklistOutsideTrial: isSelectedChecklistOutsideTrial.value,
      assignedLicenseAssignmentIds: Array.from(
        assignedLicenseAssignmentIds.value
      ),
      matchingEligibleLicense: matchingEligibleLicense.value,
      hasMatchingEligibleLicense: hasMatchingEligibleLicense.value,
      hasLinkedMatchingLicense: hasLinkedMatchingLicense.value,
      linkedOAuthLicense: linkedOAuthLicense.value,
      linkedOAuthLicenseMatchesCurriculum:
        linkedOAuthLicenseMatchesCurriculum.value,
      hasActiveLinkedMatchingLicense: hasActiveLinkedMatchingLicense.value,
      canActivateLinkedLicense: canActivateLinkedLicense.value,
      canApplyAvailableLicense: canApplyAvailableLicense.value,
      hasLicenseUnlockAction: hasLicenseUnlockAction.value,
      isPurchaseRequired: isPurchaseRequired.value,
      licenseUnlockTitle: licenseUnlockTitle.value,
      licenseUnlockActionLabel: licenseUnlockActionLabel.value,
      licenseUnlockSecondaryActionLabel:
        licenseUnlockSecondaryActionLabel.value,
      isLoadingActiveLicensesDebug: isLoadingActiveLicensesDebug.value,
      activeLicensesDebugError: activeLicensesDebugError.value,
      oauthDebug: activeLicensesDebugResponse.value?.oauthDebug,
      licenseFetchError:
        "licenseFetchError" in (activeLicensesDebugResponse.value ?? {})
          ? activeLicensesDebugResponse.value?.licenseFetchError
          : undefined,
      activeLicenses: activeLicenses.value,
      rawLicensesResponse: activeLicensesDebugResponse.value,
    },
    null,
    2
  );
});

const loadActiveLicensesDebug = async () => {
  isLoadingActiveLicensesDebug.value = true;
  activeLicensesDebugError.value = undefined;

  try {
    activeLicensesDebugResponse.value =
      await $fetch<ActiveLicensesDebugResponse>("/api/companion/licenses", {
        headers: await useApiHeaders(),
      });
  } catch (error: any) {
    if (
      isCompanionOAuthReauthenticationRequired(error) &&
      usePseudoLogin().isPseudoLogin !== true &&
      runtimeConfig.public.oauthClient.enableRedirect === true &&
      runtimeConfig.public.oauthClient.oauthAuthUrl
    ) {
      sessionStorage.setItem(SKIP_LOGOUT_REDIRECT_ONCE_KEY, "true");
      await useAppUserDataStore().logout();
      window.location.replace(runtimeConfig.public.oauthClient.oauthAuthUrl);
      return;
    }

    activeLicensesDebugResponse.value = undefined;
    activeLicensesDebugError.value =
      error?.data?.message ?? error?.message ?? "Unable to load licenses.";
  } finally {
    isLoadingActiveLicensesDebug.value = false;
  }
};

const applyMatchingLicense = async () => {
  if (
    !selectedProfileId.value ||
    !selectedCurriculum.value?.id ||
    !matchingEligibleLicense.value
  ) {
    return;
  }

  isApplyingLicense.value = true;

  try {
    await $fetch("/api/companion/licenses/apply", {
      method: "POST",
      headers: await useApiHeaders(),
      body: {
        profileId: selectedProfileId.value,
        levelId: selectedCurriculum.value.id,
        licenseAssignmentId: matchingEligibleLicense.value.id,
      },
    });
  } finally {
    isApplyingLicense.value = false;
  }
};

const activateProfileLicense = async () => {
  if (!selectedProfileId.value) {
    return;
  }

  isActivatingLicense.value = true;

  try {
    await $fetch("/api/companion/licenses/activate", {
      method: "POST",
      headers: await useApiHeaders(),
      body: {
        profileId: selectedProfileId.value,
      },
    });
  } finally {
    isActivatingLicense.value = false;
  }
};

const applyAndActivateLicense = async () => {
  if (!isSelectedChecklistOutsideTrial.value || !hasLicenseUnlockAction.value) {
    return;
  }

  try {
    if (canApplyAvailableLicense.value) {
      await applyMatchingLicense();
    }

    if (canApplyAvailableLicense.value || canActivateLinkedLicense.value) {
      await activateProfileLicense();
    }
    useBaseToast("License applied and activated.", "success");
    await loadActiveLicensesDebug();
  } catch (error: any) {
    useBaseToast(
      error?.data?.message ??
        error?.message ??
        "Unable to apply and activate license.",
      "error"
    );
  }
};

const createNewDocument = async () => {
  if (!selectedChecklistId.value) {
    useBaseToast("Please select a checklist.", "error");
    return;
  }

  const selectedChecklist = useUserChecklistsStore().checklists.find(
    (c) => c.id === selectedChecklistId.value
  );
  if (!selectedChecklist) {
    useBaseToast("Please select a checklist.", "error");
    return;
  }

  // First close the popup

  isCreatingDocument.value = true;

  let fileNameOptions = undefined as any;

  var submissionJob = new SubmissionJob({
    requiresReview: false,
    userId: useCurrentUID() ?? "",
    profileId: selectedProfileId.value,
    files: [],
    sheetController: useNewSheetController().value,
    uploadMethod: "create",
    documentSubmissionType: DocumentSubmissionType.finalDraft,
    submittedDocumentState: DocumentState.editing,
    attachChecklist: true,
    accessToken: "",
    id: useGenerateUID(),
    classrooms: [],
    assignments: [],
    checklists: [],
    students: [],
    classroom: undefined,
    assignment: undefined,
    checklist: selectedChecklist,
    student: undefined,
    parseBehavior: "default",
  });

  const submittedDocument = await submissionJob.createWritableFile(
    "Untitled Document",
    {
      preventNavigation: true,
    }
  );

  if (!submittedDocument) {
    useBaseToast("Oops! Something went wrong.", "error");
    return;
  }
};

const onDocumentCreated = async (documentIds: string[]) => {
  // submittedDocument.enableStudentCompare = true;
  // submittedDocument.enableStudentThesaurus = true;
  // submittedDocument.enableStudentReview = true;
  // await useDocuments().updateDocument(submittedDocument);

  // if (submittedDocument) {
  //   navigateTo(`/companion/${submittedDocument.id}`);
  // }
  if (documentIds.length == 0) {
    useBaseToast("Oops! Something went wrong.", "error");
    return;
  }

  navigateTo(useCreateCompanionPortalRoute(`/${documentIds[0]}`));
};

const isLoggingOut = ref(false);
const logout = async () => {
  isLoggingOut.value = true;

  await useAppUserDataStore().logout();
  navigateTo(useCreateCompanionPortalRoute("/auth/login"));
};

onMounted(async () => {
  if (allLevels.value.length === 0) {
    await levelsStore.initializeLevels();
  }

  await loadActiveLicensesDebug();
});
</script>

<template>
  <div class="h-screen w-screen flex flex-col">
    <div
      class="h-[70px] shadow border-b w-full bg-surface py-2 pl-4 flex flex-row items-center justify-between"
    >
      <img src="/img/writing-companion.png" class="h-full w-auto" />
      <div class="flex items-center gap-4 justify-end px-4">
        <CompanionActiveProfileIndicator
          :curriculum-name="selectedCurriculum?.name"
          :license-status-label="linkedLicenseStatusLabel"
          :should-confirm-curriculum-change="hasActiveLinkedMatchingLicense"
          :is-license-expired="isLinkedOAuthLicenseExpired"
          :transfer-license-assignment-id="transferableLicenseAssignmentId"
          :on-license-transferred="loadActiveLicensesDebug"
        />
        <CompanionTutorial icon-only />
        <ReportProblemButton
          source="companion"
          :show-urgency="false"
          :show-email="true"
          variant="icon"
        />
        <BaseIconButton
          @click="logout"
          :show-spinner="isLoggingOut"
          color="danger"
        >
          <BaseIconLogout />
        </BaseIconButton>
      </div>
    </div>
    <div class="grow flex flex-row min-h-0">
      <div class="w-full min-w-0 overflow-auto pb-6">
        <div class="max-w-2xl w-full mx-auto flex flex-col pt-12 min-h-0 px-4">
          <div class="mb-8 flex flex-row items-center justify-between">
            <h1 class="text-3xl font-extrabold tracking-tight text-gray-900">
              New
            </h1>
            <a v-if="shouldShowNoLicensesIntro" href="https://iew.com/Writing-Companion" target="_blank" rel="noopener noreferrer">
              <BaseTextButton color="secondary">
              <span class="flex flex-row items-center gap-2">
                Learn More  <BaseIconExternalLink class="h-4 w-4" />
              </span>
            </BaseTextButton>
            </a>
          </div>

          <CompanionTutorial />

          <div class="grid grid-cols-1 gap-8 items-start">
            <!-- Left Column: Configuration -->
            <div class="space-y-6">
              <section
                v-if="shouldShowNoLicensesIntro && false"
                class="relative overflow-hidden rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/60 via-blue-50/20 to-white p-6 shadow-sm"
              >
                <div class="flex gap-5 items-start">
                  <div
                    class="hidden sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600 shadow-sm"
                  >
                    <BaseIconBookA class="h-6 w-6" />
                  </div>
                  <div class="flex-1">
                    <p class="text-sm leading-7 text-gray-600 font-medium">
                      IEW Writing Companion is an online word processor designed
                      specifically to support IEW student writers, completely AI
                      free. When students begin, they choose the checklist that
                      matches their current assignment. As they write, they can
                      access built-in reminders and helpful word lists, making
                      it easier for them to express their ideas clearly and
                      effectively. At the end, a guided review helps students do
                      what they check and check what they do. Students can save,
                      print, or share their writing with ease.
                    </p>
                    <div
                      class="mt-5 flex items-center justify-between flex-wrap gap-4"
                    >
                      <a
                        href="https://iew.com/Writing-Companion"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-1.5 justify-center rounded-lg bg-primary-default px-4 py-2 text-sm font-semibold text-primary-on shadow-sm transition-all hover:bg-primary-shade hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                      >
                        Learn More
                        <BaseIconExternalLink class="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2
                  class="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2"
                >
                  <span
                    class="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs"
                    >1</span
                  >
                  Select Checklist
                </h2>
                <CompanionCreateSubmissionChecklistSelector
                  v-model="selectedChecklistId"
                  :can-change-curriculum-inline="canChangeCurriculumInline"
                  :is-license-expired="isLinkedOAuthLicenseExpired"
                  :license-status-label="linkedLicenseStatusLabel"
                  :has-matching-eligible-license="hasMatchingEligibleLicense"
                  @update:model-value="handleChecklistChange"
                  @checklists-updated="handleChecklistsUpdated"
                />
              </section>
            </div>

            <!-- Divider -->
            <div class="w-full flex justify-center py-2">
              <div class="h-px bg-gray-200 w-full"></div>
            </div>

            <!-- Right Column: Actions -->
            <div
              class="space-y-6 transition-all duration-300"
              :class="{
                'opacity-50 pointer-events-none grayscale-[0.5]':
                  !selectedChecklistId,
              }"
            >
              <section>
                <h2
                  class="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2"
                >
                  <span
                    class="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs"
                    >2</span
                  >
                  Start Composition
                </h2>

                <div class="relative">
                  <div class="grid grid-cols-1 gap-4">
                    <UploadStudentSubmissionButton
                      :submitted-document-state="DocumentState.editing"
                      :document-submission-type="
                        DocumentSubmissionType.finalDraft
                      "
                      :attach-checklist="true"
                      :checklist="selectedChecklist"
                      :can-create-file="true"
                      :write-only="true"
                      :requires-review="true"
                      :auto-open="false"
                      popup-alignment="end"
                      :enable-student-thesaurus="true"
                      :enable-student-review="true"
                      :profile-id="selectedProfileId"
                      @on-document-created="onDocumentCreated"
                      class="w-full"
                    >
                      <template v-slot:trigger="{ isUploading }">
                        <BaseCard
                          class="w-full text-left hover:shadow-md transition-all cursor-pointer group border border-gray-200 hover:border-primary/50 relative overflow-hidden"
                          :class="{
                            'pointer-events-none opacity-75': isUploading,
                          }"
                          @click="!isUploading"
                        >
                          <div
                            v-if="isUploading"
                            class="p-6 flex items-center justify-center gap-4 min-h-[116px]"
                          >
                            <BaseLoadingSpinner class="w-8 h-8 text-blue-600" />
                            <span class="text-gray-600 font-medium"
                              >Creating document...</span
                            >
                          </div>
                          <template v-else>
                            <div
                              class="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
                            ></div>
                            <div
                              class="p-6 flex items-center gap-6 relative z-10"
                            >
                              <div
                                class="w-16 h-16 bg-blue-100 rounded-2xl flex-shrink-0 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-sm"
                              >
                                <BaseIconEdit class="w-8 h-8 text-blue-600" />
                              </div>
                              <div class="flex-1">
                                <h3
                                  class="text-lg font-bold text-gray-900 mb-1"
                                >
                                  New Document
                                </h3>
                              </div>
                              <BaseIconChevronRight
                                class="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors"
                              />
                            </div>
                          </template>
                        </BaseCard>
                      </template>
                    </UploadStudentSubmissionButton>

                    <div class="grid grid-cols-2 gap-4">
                      <UploadStudentSubmissionButton
                        :submitted-document-state="DocumentState.editing"
                        :document-submission-type="
                          DocumentSubmissionType.finalDraft
                        "
                        :accepted-file-types="[
                          'application/vnd.google-apps.document',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        ]"
                        :attach-checklist="true"
                        :checklist="selectedChecklist"
                        :can-create-file="false"
                        :requires-review="false"
                        popup-alignment="end"
                        parse-behavior="convertToHtml"
                        :accepted-upload-methods="['computer']"
                        :accept-multiple="false"
                        :enable-student-thesaurus="true"
                        :enable-student-review="true"
                        :profile-id="selectedProfileId"
                        @on-document-created="onDocumentCreated"
                      >
                        <template #trigger="{ isUploading }">
                          <BaseCard
                            class="w-full hover:shadow-md transition-all cursor-pointer group border border-gray-200 hover:border-primary/50 relative overflow-hidden"
                            :class="{
                              'pointer-events-none opacity-75': isUploading,
                            }"
                          >
                            <div
                              v-if="isUploading"
                              class="p-5 flex flex-col items-center justify-center min-h-[140px]"
                            >
                              <BaseLoadingSpinner
                                class="w-8 h-8 text-purple-600 mb-2"
                              />
                              <span class="text-gray-600 font-medium text-sm"
                                >Uploading...</span
                              >
                            </div>
                            <template v-else>
                              <div
                                class="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
                              ></div>
                              <div
                                class="p-5 flex flex-col items-center text-center relative z-10"
                              >
                                <div
                                  class="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300"
                                >
                                  <BaseIconUpload
                                    class="w-6 h-6 text-purple-600"
                                  />
                                </div>
                                <h3 class="font-bold text-gray-900 mb-1">
                                  Upload
                                </h3>
                                <p class="text-xs text-gray-500">Computer</p>
                              </div>
                            </template>
                          </BaseCard>
                        </template>
                      </UploadStudentSubmissionButton>
                      <UploadStudentSubmissionButton
                        :submitted-document-state="DocumentState.editing"
                        :document-submission-type="
                          DocumentSubmissionType.finalDraft
                        "
                        :accepted-file-types="[
                          'application/vnd.google-apps.document',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        ]"
                        :attach-checklist="true"
                        :checklist="selectedChecklist"
                        :can-create-file="false"
                        :requires-review="false"
                        popup-alignment="end"
                        parse-behavior="convertToHtml"
                        :accepted-upload-methods="['google']"
                        :accept-multiple="false"
                        :enable-student-thesaurus="true"
                        :enable-student-review="true"
                        :profile-id="selectedProfileId"
                        @on-document-created="onDocumentCreated"
                      >
                        <template #trigger="{ isUploading }">
                          <BaseCard
                            class="hover:shadow-md transition-all cursor-pointer group border border-gray-200 hover:border-primary/50 relative overflow-hidden"
                            @click="$emit('upload-drive')"
                            :class="{
                              'pointer-events-none opacity-75': isUploading,
                            }"
                          >
                            <div
                              v-if="isUploading"
                              class="p-5 flex flex-col items-center justify-center min-h-[140px]"
                            >
                              <BaseLoadingSpinner
                                class="w-8 h-8 text-green-600 mb-2"
                              />
                              <span class="text-gray-600 font-medium text-sm"
                                >Processing...</span
                              >
                            </div>
                            <template v-else>
                              <div
                                class="absolute inset-0 bg-gradient-to-br from-green-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
                              ></div>
                              <div
                                class="p-5 flex flex-col items-center text-center relative z-10"
                              >
                                <div
                                  class="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300"
                                >
                                  <BaseIconGoogleFile
                                    class="w-6 h-6 text-green-600"
                                  />
                                </div>
                                <h3 class="font-bold text-gray-900 mb-1">
                                  Upload
                                </h3>
                                <p class="text-xs text-gray-500">
                                  Google Drive
                                </p>
                              </div>
                            </template>
                          </BaseCard>
                        </template>
                      </UploadStudentSubmissionButton>
                    </div>
                  </div>

                  <CompanionCreateSubmissionLicenseUnlockOverlay
                    v-if="shouldShowLicenseUnlockOverlay"
                    :title="licenseUnlockTitle"
                    :message="licenseUnlockMessage"
                    :action-label="licenseUnlockActionLabel"
                    :secondary-action-label="licenseUnlockSecondaryActionLabel"
                    :secondary-action-href="selectedCurriculum?.external_purchasing_url ?? 'https://iew.com'"
                    :is-loading="isLicenseUnlockActionLoading"
                    :is-purchase-required="isPurchaseRequired"
                    @action="applyAndActivateLicense"
                  />
                </div>
              </section>
            </div>
          </div>

          <section
            v-if="false"
            class="mt-8 rounded border border-amber-300 bg-amber-50 p-4"
          >
            <div class="mb-3 flex items-center justify-between gap-4">
              <h2
                class="text-sm font-bold uppercase tracking-wide text-amber-900"
              >
                Temporary License Debug
              </h2>
              <BaseButton
                size="sm"
                :show-spinner="isLoadingActiveLicensesDebug"
                @click="loadActiveLicensesDebug"
              >
                Refresh
              </BaseButton>
            </div>
            <pre
              class="max-h-[420px] overflow-auto rounded bg-gray-950 p-4 text-left text-xs leading-relaxed text-amber-50"
              >{{ activeLicensesDebugJson }}</pre
            >
          </section>
        </div>
      </div>
      <!-- Sidebar -->
      <CompanionSidebarContainer
        :is-open="true"
        class="border-r border-gray-200 shadow-sm transition-all duration-300 w-full max-w-[350px]"
      />
    </div>
  </div>
</template>
